<div class="ai-admin-wrapper">

  <!-- Header -->
  <div class="page-header">
    <div class="header-left">
      <span class="label-rnd">R&amp;D LAB</span>
      <h1>AI Admin Assistant</h1>
      <p>Type a natural language command to configure GRMIS. The AI interprets, you approve.</p>
    </div>
    <div class="tab-bar">
      <button class="tab-btn" [class.active]="activeTab === 'assistant'" (click)="activeTab = 'assistant'; reset()">
        <span class="material-icons">smart_toy</span> Assistant
      </button>
      <button class="tab-btn" [class.active]="activeTab === 'history'" (click)="activeTab = 'history'; loadHistory()">
        <span class="material-icons">history</span> History
      </button>
    </div>
  </div>

  <!-- ── ASSISTANT TAB ─────────────────────────────────────────────────── -->
  <ng-container *ngIf="activeTab === 'assistant'">

    <!-- Model status banner -->
    <div class="model-banner loading" *ngIf="modelLoading">
      <span class="material-icons spin">autorenew</span>
      <span>Loading Gemma 4 12B into GPU memory&hellip; this takes 1&ndash;2 minutes.</span>
    </div>

    <div class="model-banner not-loaded" *ngIf="!modelLoaded && !modelLoading">
      <span class="material-icons">memory</span>
      <span>Gemma model is not loaded.
        <ng-container *ngIf="modelError"> Error: {{ modelError }}.</ng-container>
      </span>
      <button class="btn-primary btn-sm" (click)="loadModel()">
        <span class="material-icons">download</span> Load Model
      </button>
    </div>

    <!-- Idle / Input state -->
    <div class="card input-card" *ngIf="state === 'idle' || state === 'error'">
      <h2><span class="material-icons">edit</span> Enter Command</h2>
      <textarea
        class="command-input"
        rows="3"
        placeholder="e.g. Add a complaint category called Road Damage under Public Works with 7 day SLA"
        [(ngModel)]="command"
        (keydown.enter)="$event.preventDefault(); submit()"
        maxlength="1000">
      </textarea>
      <div class="input-footer">
        <span class="char-count">{{ command.length }} / 1000</span>
        <button class="btn-primary" [disabled]="!command.trim() || !modelLoaded" (click)="submit()">
          <span class="material-icons">send</span> Analyze
        </button>
      </div>

      <div class="error-box" *ngIf="state === 'error' && errorMessage">
        <span class="material-icons">error_outline</span>
        {{ errorMessage }}
      </div>

      <div class="examples">
        <p class="examples-label">Try these examples:</p>
        <button class="example-chip" *ngFor="let ex of exampleCommands" (click)="useExample(ex)">{{ ex }}</button>
      </div>
    </div>

    <!-- Thinking state -->
    <div class="card thinking-card" *ngIf="state === 'thinking'">
      <div class="thinking-spinner">
        <span class="material-icons spin">autorenew</span>
        <p>Gemma 4 is analyzing your command&hellip;</p>
      </div>
    </div>

    <!-- Preview state -->
    <div class="card preview-card" *ngIf="state === 'preview' && pending">
      <div class="preview-header">
        <span class="material-icons action-icon">{{ actionIcon }}</span>
        <div>
          <h2>{{ actionLabel }}</h2>
          <p class="raw-command">&ldquo;{{ pending.rawCommand }}&rdquo;</p>
        </div>
        <div class="confidence-badge" [class]="confidenceClass">
          <span class="material-icons">psychology</span>
          {{ confidencePercent }}% confidence
        </div>
      </div>

      <div class="action-data">
        <h3>Interpreted Action</h3>
        <div class="data-grid">
          <div class="data-row" *ngFor="let entry of dataEntries">
            <span class="data-key">{{ formatKey(entry.key) }}</span>
            <span class="data-value">{{ entry.value }}</span>
          </div>
        </div>
      </div>

      <div class="warning-box" *ngIf="confidencePercent < 70">
        <span class="material-icons">warning_amber</span>
        Low confidence — review the interpreted action carefully before approving.
      </div>

      <div class="reject-input" *ngIf="false">
        <input type="text" placeholder="Reason for rejection (optional)" [(ngModel)]="rejectReason" />
      </div>

      <div class="preview-actions">
        <button class="btn-danger" (click)="showReject()">
          <span class="material-icons">close</span> Reject
        </button>
        <button class="btn-secondary" (click)="reset()">
          <span class="material-icons">edit</span> Edit Command
        </button>
        <button class="btn-primary" (click)="approve()">
          <span class="material-icons">check_circle</span> Approve &amp; Apply
        </button>
      </div>
    </div>

    <!-- Applying state -->
    <div class="card thinking-card" *ngIf="state === 'applying'">
      <div class="thinking-spinner">
        <span class="material-icons spin">sync</span>
        <p>Applying change to GRMIS&hellip;</p>
      </div>
    </div>

    <!-- Done state -->
    <div class="card done-card" *ngIf="state === 'done' && pending">
      <span class="material-icons done-icon">check_circle</span>
      <h2>Change Applied</h2>
      <p class="done-action">{{ actionLabel }}</p>
      <div class="data-grid done-data">
        <div class="data-row" *ngFor="let entry of dataEntries">
          <span class="data-key">{{ formatKey(entry.key) }}</span>
          <span class="data-value">{{ entry.value }}</span>
        </div>
      </div>
      <div class="preview-actions">
        <button class="btn-primary" (click)="reset(); command = ''">
          <span class="material-icons">add</span> New Command
        </button>
        <button class="btn-secondary" (click)="activeTab = 'history'; loadHistory()">
          <span class="material-icons">history</span> View History
        </button>
      </div>
    </div>

  </ng-container>

  <!-- ── HISTORY TAB ────────────────────────────────────────────────────── -->
  <ng-container *ngIf="activeTab === 'history'">
    <div class="card history-card">
      <div class="history-header">
        <h2><span class="material-icons">history</span> Command History</h2>
        <span class="total-badge">{{ historyTotal }} total</span>
      </div>

      <div class="loading-row" *ngIf="historyLoading">
        <span class="material-icons spin">autorenew</span> Loading&hellip;
      </div>

      <div class="history-empty" *ngIf="!historyLoading && history.length === 0">
        <span class="material-icons">inbox</span>
        <p>No commands yet.</p>
      </div>

      <div class="history-table" *ngIf="!historyLoading && history.length > 0">
        <div class="history-row header-row">
          <span>Date</span>
          <span>Command</span>
          <span>Action</span>
          <span>Confidence</span>
          <span>Status</span>
          <span></span>
        </div>
        <button type="button" class="history-row clickable-row" *ngFor="let cmd of history"
                (click)="openDetail(cmd)">
          <span class="col-date">{{ cmd.createdAt | date:'dd MMM HH:mm' }}</span>
          <span class="col-cmd" [title]="cmd.rawCommand">{{ cmd.rawCommand | slice:0:60 }}{{ cmd.rawCommand.length > 60 ? '…' : '' }}</span>
          <span class="col-action">{{ ACTION_LABELS[cmd.actionType] ?? cmd.actionType }}</span>
          <span class="col-conf">{{ (cmd.aiConfidence * 100) | number:'1.0-0' }}%</span>
          <span class="status-pill" [class]="statusClass(cmd.status)">{{ cmd.status }}</span>
          <span class="col-chevron"><span class="material-icons">chevron_right</span></span>
        </button>
      </div>

      <div class="pagination" *ngIf="historyTotal > 15">
        <button class="btn-secondary" [disabled]="historyPage === 0" (click)="loadHistory(historyPage - 1)">
          <span class="material-icons">chevron_left</span>
        </button>
        <span>Page {{ historyPage + 1 }}</span>
        <button class="btn-secondary" [disabled]="(historyPage + 1) * 15 >= historyTotal" (click)="loadHistory(historyPage + 1)">
          <span class="material-icons">chevron_right</span>
        </button>
      </div>
    </div>
  </ng-container>

</div>

<!-- ── DETAIL DRAWER ─────────────────────────────────────────────────────── -->
<div class="detail-overlay" *ngIf="selectedCmd"
     (click)="closeDetail()" (keydown.escape)="closeDetail()">
  <div class="detail-drawer"
       (click)="$event.stopPropagation()" (keydown)="$event.stopPropagation()">

    <!-- Drawer header -->
    <div class="drawer-header">
      <div class="drawer-title">
        <span class="material-icons drawer-action-icon">{{ ACTION_ICONS[selectedCmd.actionType] ?? 'smart_toy' }}</span>
        <div>
          <h2>{{ ACTION_LABELS[selectedCmd.actionType] ?? selectedCmd.actionType }}</h2>
          <span class="status-pill" [class]="statusClass(selectedCmd.status)">{{ selectedCmd.status }}</span>
        </div>
      </div>
      <button type="button" class="close-btn" (click)="closeDetail()">
        <span class="material-icons">close</span>
      </button>
    </div>

    <!-- Loading -->
    <div class="drawer-loading" *ngIf="detailLoading">
      <span class="material-icons spin">autorenew</span>
    </div>

    <!-- Drawer body -->
    <div class="drawer-body" *ngIf="!detailLoading">

      <!-- Full command -->
      <div class="detail-section">
        <p class="section-label">Command</p>
        <p class="full-command">&ldquo;{{ selectedCmd.rawCommand }}&rdquo;</p>
      </div>

      <!-- AI confidence -->
      <div class="detail-section">
        <p class="section-label">AI Analysis</p>
        <div class="detail-meta-row">
          <span>Confidence</span>
          <span class="confidence-badge" [class]="confidenceClassFor(selectedCmd.aiConfidence)">
            <span class="material-icons">psychology</span>
            {{ (selectedCmd.aiConfidence * 100) | number:'1.0-0' }}%
          </span>
        </div>
      </div>

      <!-- Action data -->
      <div class="detail-section" *ngIf="selectedCmd.actionData && actionDataEntries(selectedCmd.actionData).length">
        <p class="section-label">Interpreted Action</p>
        <div class="data-grid">
          <div class="data-row" *ngFor="let entry of actionDataEntries(selectedCmd.actionData)">
            <span class="data-key">{{ formatKey(entry.key) }}</span>
            <span class="data-value">{{ entry.value }}</span>
          </div>
        </div>
      </div>

      <!-- Timestamps -->
      <div class="detail-section">
        <p class="section-label">Timeline</p>
        <div class="detail-meta-row">
          <span>Submitted</span>
          <span>{{ selectedCmd.createdAt | date:'dd MMM yyyy, HH:mm' }}</span>
        </div>
        <div class="detail-meta-row" *ngIf="selectedCmd.approvedAt">
          <span>Approved</span>
          <span>{{ selectedCmd.approvedAt | date:'dd MMM yyyy, HH:mm' }}</span>
        </div>
        <div class="detail-meta-row" *ngIf="selectedCmd.appliedAt">
          <span>Applied</span>
          <span>{{ selectedCmd.appliedAt | date:'dd MMM yyyy, HH:mm' }}</span>
        </div>
      </div>

      <!-- Rejection reason -->
      <div class="detail-section" *ngIf="selectedCmd.rejectionReason">
        <p class="section-label">Rejection Reason</p>
        <p class="rejection-text">{{ selectedCmd.rejectionReason }}</p>
      </div>

      <!-- Error -->
      <div class="detail-section" *ngIf="selectedCmd.errorMessage">
        <p class="section-label">Error</p>
        <p class="error-text">{{ selectedCmd.errorMessage }}</p>
      </div>

      <!-- Audit trail -->
      <div class="detail-section" *ngIf="selectedCmd.auditLogs?.length">
        <p class="section-label">Audit Trail</p>
        <div class="audit-timeline">
          <div class="audit-entry" *ngFor="let log of selectedCmd.auditLogs">
            <div class="audit-dot" [class]="auditDotClass(log.eventType)"></div>
            <div class="audit-body">
              <span class="audit-event">{{ log.eventType }}</span>
              <span class="audit-detail">{{ log.eventDetail }}</span>
              <span class="audit-time">{{ log.createdAt | date:'dd MMM HH:mm:ss' }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</div>
